import { DateTime } from "luxon";
import * as yup from "yup";

export type QuotationRequestRQ = {
  lang: string;
  source_entity: string;
  target_entity: string;
  message: string;
  title: string;
  adults: number;
  children: number;
  required_services: ("FL" | "ACC" | "TRA" | "FER" | "TRF" | "ACT")[];
  offer_due_date: string;
  simulate_failure?: boolean;
  file: File | null;
};

export function createQuotationRequestRQ(
  source_entity: string,
  target_entity: string,
  initialMessage: string,
  simulate_failure?: boolean,
): QuotationRequestRQ {
  const payload: QuotationRequestRQ = {
    lang: "en",
    source_entity,
    target_entity,
    message: !!initialMessage ? initialMessage : "",
    title: "",
    adults: 2,
    children: 0,
    required_services: ["FL", "ACC", "TRA", "FER", "TRF", "ACT"],
    offer_due_date: DateTime.now().plus({ days: 2 }).toISODate(),
    file: null,
  };

  if (simulate_failure) {
    payload.simulate_failure = true;
  }

  return payload;
}

export const quotationRequestRQValidationSchema = yup.object().shape({
  lang: yup.string().required().default("en"),
  source_entity: yup.string().required(),
  target_entity: yup.string().required(),
  message: yup
    .string()
    .min(200)
    .test("if-file-is-chosen-message-is-nullable", "Message is required", (value, context) => {
      const { file } = context.parent as QuotationRequestRQ;
      if (!!file) {
        return true;
      }
      return value !== null && typeof value === "string" && value.length >= 200;
    }),
  title: yup.string().required().min(10),
  adults: yup.number().required().min(1),
  children: yup.number().required().min(0),
  required_services: yup.array().of(yup.string()).required(),
});

type QuotationRequestImage = {
  image_id: string;
  filename: string;
  qhd: string;
  fhd: string;
  hd: string;
  sd: string;
};

type DestinationType = "NOR" | "LAY" | "NON";
type DestinationTypeVerbose = "Normal" | "Layover" | "Non-serviced";
export const DestinationTypeMapping: Record<DestinationType, DestinationTypeVerbose> = {
  NOR: "Normal",
  LAY: "Layover",
  NON: "Non-serviced",
};

export type QuotationRequestDestination = {
  id: number;
  code: string;
  dest_type: "NOR" | "NON" | "LAY";
  point_type: "destination" | "origin" | "return";
  stay: number;
  images: QuotationRequestImage[];
  geodata: {
    lat: number;
    lng: number;
  };
  name_cn: string;
  name_en: string;
  date_from: string;
  date_to: string;
  order: number;
  trip_index_days: number[];
  dates: string[];
};

type SERVICE_TYPES = "ACC" | "TRF" | "FL" | "MI" | "COO" | "ACT" | "FER" | "COA" | "TRA";

type ROOM_TYPES = "STD" | "DEL" | "EXC" | "SUI";
type BEDDING_TYPES = "DBL" | "TWI";
type BOARD_TYPES = "BB" | "HB" | "FB" | "AI" | "RO";
type BOARD_TYPES_VERBOSE =
  | "Bed & Breakfast"
  | "Half Board"
  | "Full Board"
  | "All Inclusive"
  | "Room Only";
export const BoardTypeMapping: Record<BOARD_TYPES, BOARD_TYPES_VERBOSE> = {
  BB: "Bed & Breakfast",
  HB: "Half Board",
  FB: "Full Board",
  AI: "All Inclusive",
  RO: "Room Only",
};

type HotelDef = {
  label: string;
  uid: string;
};

export type HotelPreferences = {
  board: BOARD_TYPES[];
  hotel: HotelDef;
  rating: number[];
  bedding: BEDDING_TYPES[];
  room_type: ROOM_TYPES[];
  client_own_arrangement: boolean;
};

type LANGUAGE_TYPES = "en" | "fr" | "el" | "pt" | "es" | "zh";
type LANGUAGE_TYPES_VERBOSE = "English" | "French" | "Greek" | "Portuguese" | "Spanish" | "Chinese";
export const LanguageTypeMapping: Record<LANGUAGE_TYPES, LANGUAGE_TYPES_VERBOSE> = {
  en: "English",
  fr: "French",
  el: "Greek",
  pt: "Portuguese",
  es: "Spanish",
  zh: "Chinese",
};

export type CoordinatorDef = {
  languages: LANGUAGE_TYPES[];
  skills: ("driver" | "guide" | "interpreter")[];
  inclusions: ("meals" | "tips" | "accommodation" | "entrance_fees")[];
  exclusions: ("meals" | "tips" | "accommodation" | "entrance_fees")[];
  hours: number;
};

type POIDef = {
  label: string;
  uid: string;
};

export type ActivityPreferencesDef = {
  poi?: POIDef;
  number_of_activities: "HIG" | "MED" | "LOW";
  activity_type:
    | "sightseeing"
    | "art_and_culture"
    | "attractions_and_shows"
    | "experiences"
    | "local_food"
    | "western"
    | "chinese"
    | "italian"
    | "mexican"
    | "vegetarian"
    | "street_food"
    | "fine_dining"
    | "local_brands"
    | "flea_market"
    | "luxury_brands";
  meal_types: "BRE" | "LUN" | "DIN";
  client_own_arrangement: boolean;
};

type TRANSFER_TYPES = "PRV" | "SHA";

export type TransferPreferencesDef = {
  transfer_type: TRANSFER_TYPES;
  big_luggage_capacity: number;
  small_luggage_capacity: number;
};

export type ServiceRequirement = {
  uid: string;
  notes: string;
  preferences: ActivityPreferencesDef | TransferPreferencesDef | HotelPreferences | CoordinatorDef;
  service_type: SERVICE_TYPES;
  day: number;
  service_date: string;
};

type FlightPreferencesDef = {
  stops: "DI" | "MF" | "ALL";
  class_: "ECONOMY" | "PREMIUM ECONOMY" | "BUSINESS" | "FIRST" | "ALL";
  arrival: "ALL" | "SD";
  flight_type: "GDS" | "LCC";
};

type FlightServiceRequirement = {
  notes: string;
  preferences: FlightPreferencesDef;
  service_type: "FL";
};

type TrainPreferencesDef = {
  seat_class: "ECONOMY" | "BUSSINES" | "FIRST" | "ALL";
  cabin: "DOUBLE" | "TRIPLE" | "QUADRUPLE" | "SUITE" | "ALL";
  train_type: "REG" | "HIG" | "ALL";
};

type TrainServiceRequirement = {
  notes: string;
  preferences: TrainPreferencesDef;
  service_type: "TRA";
};

type FerryPreferencesDef = {
  seat_class: "ECONOMY" | "BUSSINES" | "OPEN DECK" | "CLOSED DECK" | "ALL";
  cabin: "DOUBLE" | "TRIPLE" | "QUADRUPLE" | "SUITE" | "ALL";
  vessel_type: "regular" | "high-speed" | "ALL";
};

type FerryServiceRequirement = {
  notes: string;
  preferences: FerryPreferencesDef;
  service_type: "FER";
};

type CoachPreferencesDef = {
  seats: 14 | 44;
  vehicles: number;
  facilities: "WIFI" | "WC";
};

type CoachServiceRequirement = {
  notes: string;
  preferences: CoachPreferencesDef;
  service_type: "COA";
};

export type TripLeg = {
  uid: string;
  departure: string;
  arrival: string;
  leg_type: "inbetween" | "extremal";
  origin_code: string;
  origin_order: number;
  destination_code: string;
  destination_order: number;
  service_preference:
    | FlightServiceRequirement
    | TrainServiceRequirement
    | FerryServiceRequirement
    | CoachServiceRequirement;
};

type QuotationRequestType = {
  title: string;
  adults: number;
  children: number;
  rooms: number;
  coordinators: number;
  trip_start_date: string;
  contact_person_title: string;
  contact_person_first_name: string;
  contact_person_last_name: string;
  contact_person_email: string;
  contact_person_phone: string;
  budget: number;
  required_services: string[];
  service_requirements: Record<string, ServiceRequirement[]>;
  legs: Record<string, TripLeg>;
  destinations: QuotationRequestDestination[];
  due_date: string;
  sent_date: string;
  assign_date: string;
  description: string;
  internal_notes: string;
  reject_note: string;
  status: string;
  excluded_service_uids: string[];
};

export type QuotationRequestSessionType = {
  source_entity: string;
  request: QuotationRequestRQ;
  session_id: string;
  created_at: string;
  quotation_request: QuotationRequestType;
  status: "COMPLETED" | "FAILED" | "PENDING";
  error_message: string | null;
  generation_progress: number;
  generation_progress_message: string;
};
